import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

/**
 * The single button primitive for the whole site.
 *
 * variant:
 *   brand, solid brand green, dark text. The main call to action.
 *   ink, solid deep navy. Secondary call to action on light surfaces.
 *   outline, hairline border, for light surfaces.
 *   outlineOnDark, hairline border, for the dark shell.
 *   ghost, text-only with an arrow affordance.
 */
const VARIANTS = {
  brand: 'bg-brand text-brand-900 hover:bg-brand-300 shadow-sm',
  ink: 'bg-ink-800 text-white hover:bg-ink-700',
  outline: 'border border-[var(--fbs-line)] bg-white text-ink-700 hover:border-ink-700',
  outlineOnDark: 'border border-white/25 text-white hover:border-white/60 hover:bg-white/[0.06]',
  ghost: 'text-ink-700 hover:text-brand-800 px-0',
};

const SIZES = {
  sm: 'text-[13.5px] px-4 py-2.5',
  md: 'text-[14.5px] px-6 py-3.5',
  lg: 'text-[15px] px-7 py-4',
};

export default function Button({
  children,
  href,
  variant = 'brand',
  size = 'md',
  withArrow = false,
  className = '',
  ...props
}) {
  const classes = [
    'inline-flex items-center justify-center gap-2 rounded-full font-semibold',
    'transition-colors duration-200 whitespace-nowrap cursor-pointer',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    VARIANTS[variant] || VARIANTS.brand,
    SIZES[size] || SIZES.md,
    className,
  ].join(' ');

  const content = (
    <>
      {children}
      {withArrow && <ArrowRight data-component-id="src/components/ui/Button.jsx:49:20" data-component-path="src/components/ui/Button.jsx" data-component-line="49" data-component-end-line="49" data-component-file="Button.jsx" data-component-name="ArrowRight" data-component-content="%7B%22elementName%22%3A%22ArrowRight%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" aria-hidden="true" />}
    </>
  );

  if (href) {
    return (
      <Link data-component-id="src/components/ui/Button.jsx:55:6" data-component-path="src/components/ui/Button.jsx" data-component-line="55" data-component-end-line="57" data-component-file="Button.jsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%5Bvar%3Ahref%5D%22%2C%22className%22%3A%22%5Bvar%3Aclasses%5D%22%7D" href={href} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button data-component-id="src/components/ui/Button.jsx:62:4" data-component-path="src/components/ui/Button.jsx" data-component-line="62" data-component-end-line="64" data-component-file="Button.jsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22%5Bvar%3Aclasses%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" className={classes} {...props}>
      {content}
    </button>
  );
}
